import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as _ from 'lodash-es';

/**
 * Shared service for Non-Financial Information (Direct Owner & Tax Owner).
 * Matches AngularJS NonFinancialFactory: loadJsonObject (GET), saveDirectOwner, saveTaxOwner (POST).
 */
@Injectable({
  providedIn: 'root'
})
export class NonFinancialInformationService {

  constructor(private http: HttpClient) {}

  /**
   * Load owner details (current direct or tax owner list).
   * Action code 'eoowhg'. Payload must match: action_code, activity_key, attrib_name,
   * combination_key, filing_key, is_locked, issue_id, issue_short_desc, jcd_key, jcd_ta_key,
   * parent_me_string, scenario, scenario_v1, screen_key, screen_type, tax_year.
   * Uses GET for loadJsonObject.
   */
  loadOwnerDetails(
    baseUrl: string,
    combinationKey: string,
    attribName: 'direct_owner' | 'tax_owner',
    globalService: any,
    filterParams: any,
    rowData: any
  ): Observable<any> {
    const gp = globalService || {};
    const fp = filterParams || {};
    const rd = rowData || {};

    const params: any = _.merge({}, gp, fp);
    params.action_code = 'eoowhg';
    params.activity_key = fp.activity_key ?? gp.activity_key ?? 324;
    params.attrib_name = attribName;
    params.combination_key = combinationKey;
    params.filing_key = fp.filing_key ?? gp.filing_key ?? rd.FILING_KEY ?? rd.CONSOL_GROUP_KEY ?? 1;
    params.is_locked = fp.is_locked ?? gp.is_locked ?? 'N';
    params.issue_id = fp.issue_id ?? gp.issue_id ?? '';
    params.issue_id_for_tx_save = fp.issue_id_for_tx_save ?? gp.issue_id_for_tx_save ?? '';
    params.issue_short_desc = fp.issue_short_desc ?? gp.issue_short_desc ?? '';
    params.jcd_key = fp.jcd_key ?? gp.jcd_key ?? rd.JCD_KEY ?? 250;
    params.jcd_ta_key = fp.jcd_ta_key ?? gp.jcd_ta_key ?? 1110;
    params.parent_me_string = fp.parent_me_string ?? gp.parent_me_string ?? '';
    params.scenario = fp.scenario ?? gp.scenario ?? rd.SCENARIO ?? '';
    params.scenarioCode = fp.scenarioCode ?? gp.scenarioCode ?? 'RAF';
    params.scenario_v1 = fp.scenario_v1 ?? gp.scenario_v1 ?? params.scenario ?? '';
    params.screen_key = fp.screen_key ?? gp.screen_key ?? 180;
    params.screen_type = fp.screen_type ?? gp.screen_type ?? 'grid';
    params.tax_year = fp.tax_year ?? gp.tax_year ?? rd.TAX_YEAR ?? '';

    const base = (baseUrl || '').replace(/\/$/, '');
    const url = `${base}/loadJsonObject`;
    return this.getParams(url, params, globalService);
  }

  /**
   * Load assignable owner list (for dropdown).
   * Action code '2kw9yc'. Payload must match: action_code, activity_key, combination_key,
   * filing_key, is_locked, issue_id, issue_short_desc, jcd_key, jcd_ta_key, leid,
   * parent_me_string, scenario, scenario_v1, screen_key, screen_type, tax_year.
   * Direct: leid = rowData.LEID; Tax: leid = 'Tax_owner'. Uses GET for loadJsonObject.
   */
  loadOwnerList(
    baseUrl: string,
    combinationKey: string,
    leid: string,
    globalService: any,
    filterParams: any,
    rowData: any
  ): Observable<any> {
    const gp = globalService || {};
    const fp = filterParams || {};
    const rd = rowData || {};

    const params: any = _.merge({}, gp, fp);
    params.action_code = '2kw9yc';
    params.activity_key = fp.activity_key ?? gp.activity_key ?? 324;
    params.combination_key = combinationKey;
    params.filing_key = fp.filing_key ?? gp.filing_key ?? rd.FILING_KEY ?? rd.CONSOL_GROUP_KEY ?? 1;
    params.is_locked = fp.is_locked ?? gp.is_locked ?? 'N';
    params.issue_id = fp.issue_id ?? gp.issue_id ?? '';
    params.issue_id_for_tx_save = fp.issue_id_for_tx_save ?? gp.issue_id_for_tx_save ?? '';
    params.issue_short_desc = fp.issue_short_desc ?? gp.issue_short_desc ?? '';
    params.jcd_key = fp.jcd_key ?? gp.jcd_key ?? rd.JCD_KEY ?? 250;
    params.jcd_ta_key = fp.jcd_ta_key ?? gp.jcd_ta_key ?? 1110;
    params.leid = leid;
    params.parent_me_string = fp.parent_me_string ?? gp.parent_me_string ?? '';
    params.scenario = fp.scenario ?? gp.scenario ?? rd.SCENARIO ?? '';
    params.scenario_v1 = fp.scenario_v1 ?? gp.scenario_v1 ?? params.scenario ?? '';
    params.screen_key = fp.screen_key ?? gp.screen_key ?? 180;
    params.screen_type = fp.screen_type ?? gp.screen_type ?? 'grid';
    params.tax_year = fp.tax_year ?? gp.tax_year ?? rd.TAX_YEAR ?? '';

    const base = (baseUrl || '').replace(/\/$/, '');
    const url = `${base}/loadJsonObject`;
    return this.getParams(url, params, globalService);
  }

  /**
   * Save direct owner. Action code q44xa7, process_name save_Direct_Owner.
   * Payload matches: jsonSettings, jsonObj, tax_year, screen_key, scenario, jcd_key, jcd_ta_key,
   * activity_key, screen_type, is_locked, issue_id, issue_short_desc, scenario_v1, checkLockedbj,
   * scenarioCode, is_issue_key_enabled, filing_key, parent_me_string, process_name.
   */
  saveDirectOwner(
    baseUrl: string,
    scheduleSettings: any[],
    scheduleDetails: any,
    globalService: any,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=q44xa7`;
    const params = this.buildSaveParams(
      scheduleSettings,
      scheduleDetails,
      globalService,
      filterParams,
      rowData,
      'save_Direct_Owner'
    );
    return this.postParams(url, params, globalService);
  }

  /**
   * Save tax owner. Action code q44xa7, process_name save_Tax_Owner. Same payload shape.
   */
  saveTaxOwner(
    baseUrl: string,
    scheduleSettings: any[],
    scheduleDetails: any,
    globalService: any,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=q44xa7`;
    const params = this.buildSaveParams(
      scheduleSettings,
      scheduleDetails,
      globalService,
      filterParams,
      rowData,
      'save_Tax_Owner'
    );
    return this.postParams(url, params, globalService);
  }

  /**
   * Build payload for q44xa7 save (action_code q44xa7).
   * Matches: jsonSettings, jsonObj, tax_year, screen_key, scenario, jcd_key, jcd_ta_key,
   * activity_key, screen_type, is_locked, issue_id, issue_short_desc, issue_id_for_tx_save,
   * checkLockedbj, scenarioCode, is_issue_key_enabled, filing_key, parent_me_string, process_name.
   */
  private buildSaveParams(
    scheduleSettings: any[],
    scheduleDetails: any,
    globalService: any,
    filterParams: any,
    rowData: any,
    processName: string
  ): any {
    const gp = globalService || {};
    const fp = filterParams || {};
    const rd = rowData || {};

    const taxYear = fp.tax_year ?? gp.tax_year ?? rd.TAX_YEAR ?? '';
    const scenario = fp.scenario ?? gp.scenario ?? rd.SCENARIO ?? '';
    const scenarioKey = typeof scenario === 'number' ? scenario : parseInt(String(scenario), 10) || 0;
    const combinationKey = rd.COMBINATION_KEY ?? rd.combination_key ?? '';
    const combinationKeyNum = typeof combinationKey === 'number' ? combinationKey : parseInt(String(combinationKey), 10) || 0;

    const checkLockedbj = [{
      leid: rd.LEID ?? rd.leid ?? '',
      cdr_number: rd.CDR_NO ?? rd.cdr_number ?? rd.MARS ?? '',
      reporting_period: Number(rd.REPORTING_PERIOD ?? rd.reporting_period ?? 1),
      combination_key: combinationKeyNum || combinationKey,
      scenario_key: scenarioKey
    }];

    const params: any = _.merge({}, gp, fp);

    params.jsonSettings = JSON.stringify(scheduleSettings);
    params.jsonObj = typeof scheduleDetails === 'string' ? scheduleDetails : JSON.stringify(scheduleDetails);
    params.tax_year = taxYear;
    params.screen_key = fp.screen_key ?? gp.screen_key ?? rd.SCREEN_KEY ?? 180;
    params.scenario = scenario;
    params.jcd_key = fp.jcd_key ?? gp.jcd_key ?? rd.JCD_KEY ?? 250;
    params.jcd_ta_key = fp.jcd_ta_key ?? gp.jcd_ta_key ?? rd.JCD_TA_KEY ?? 1110;
    params.activity_key = fp.activity_key ?? gp.activity_key ?? rd.ACTIVITY_KEY ?? 324;
    params.screen_type = fp.screen_type ?? gp.screen_type ?? 'grid';
    params.is_locked = fp.is_locked ?? gp.is_locked ?? rd.IS_LOCKED ?? 'N';
    params.issue_id = fp.issue_id ?? gp.issue_id ?? rd.ISSUE_ID ?? '';
    params.issue_short_desc = fp.issue_short_desc ?? gp.issue_short_desc ?? rd.ISSUE_SHORT_DESC ?? '';
    params.issue_id_for_tx_save = fp.issue_id_for_tx_save ?? gp.issue_id_for_tx_save ?? rd.ISSUE_ID_FOR_TX_SAVE ?? '';
    params.checkLockedbj = JSON.stringify(checkLockedbj);
    params.scenarioCode = fp.scenarioCode ?? gp.scenarioCode ?? 'RAF';
    params.is_issue_key_enabled = fp.is_issue_key_enabled ?? gp.is_issue_key_enabled ?? 'N';
    params.filing_key = fp.filing_key ?? gp.filing_key ?? rd.FILING_KEY ?? rd.CONSOL_GROUP_KEY ?? 1;
    params.parent_me_string = fp.parent_me_string ?? gp.parent_me_string ?? rd.PARENT_ME_STRING ?? '';
    params.process_name = processName;

    return params;
  }

  /** Query param order to match URL: .../loadJsonObject?action_code=...&activity_key=...&filing_key=...&... */
  private readonly loadJsonObjectParamOrder = [
    'action_code', 'activity_key', 'filing_key', 'combination_key', 'is_locked',
    'issue_id', 'issue_id_for_tx_save', 'issue_short_desc', 'jcd_key', 'jcd_ta_key',
    'leid', 'attrib_name', 'parent_me_string', 'scenario', 'scenario_v1', 'screen_key', 'screen_type', 'tax_year'
  ];

  private getParams(url: string, params: any, globalService: any): Observable<any> {
    const requestParams = { ...(globalService || {}), ...params };

    const parts: string[] = [];
    const keys = this.loadJsonObjectParamOrder.filter(k => requestParams.hasOwnProperty(k));
    const rest = Object.keys(requestParams).filter(k => !this.loadJsonObjectParamOrder.includes(k));
    [...keys, ...rest].forEach(key => {
      const val = requestParams[key];
      const encodedKey = encodeURIComponent(key);
      if (val === '' || val === null || val === undefined) {
        parts.push(`${encodedKey}=`);
      } else {
        parts.push(`${encodedKey}=${encodeURIComponent(String(val))}`);
      }
    });

    const queryString = parts.join('&');
    const fullUrl = queryString ? `${url}?${queryString}` : url;

    return this.http.get<any>(fullUrl, { withCredentials: true });
  }

  private postParams(url: string, params: any, globalService: any): Observable<any> {
    const requestParams = { ...(globalService || {}), ...params };

    const urlEncoded = new URLSearchParams();
    Object.keys(requestParams).forEach(key => {
      if (requestParams[key] !== undefined && requestParams[key] !== null) {
        urlEncoded.set(key, requestParams[key].toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }
}
